import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Certification,
  Complaint,
  CoverageDay,
  Customer,
  InventoryItem,
  Staff,
  StaffRequest,
} from '@/lib/types';

export type AlertSeverity = 'danger' | 'warn' | 'info';
export interface AlertItem {
  id: string;
  severity: AlertSeverity;
  title: string;
  detail: string;
  to: string;
}

const REQ_TYPE: Record<string, string> = { LEAVE: '請假', SWAP: '換班', OT: '加班' };

/** 由系統現況即時推導的站內通知（低庫存 / 證照 / 客訴 / 排班 / 人力）。 */
export function useAlerts() {
  const now = Date.now();
  const lowStock = useQuery({ queryKey: ['inv-low'], queryFn: () => api.get<InventoryItem[]>('/inventory/items/low') });
  const certs = useQuery({ queryKey: ['certs-expired'], queryFn: () => api.get<Certification[]>('/scheduling/certs/expired') });
  const requests = useQuery({ queryKey: ['sched-requests'], queryFn: () => api.get<StaffRequest[]>('/scheduling/requests') });
  const complaints = useQuery({ queryKey: ['complaints'], queryFn: () => api.get<Complaint[]>('/compliance/complaints') });
  const coverage = useQuery({ queryKey: ['coverage'], queryFn: () => api.get<CoverageDay[]>('/scheduling/coverage') });
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });

  const staffName = (id: string) => staff.data?.find((s) => s.id === id)?.name ?? '員工';
  const custName = (id: string) => customers.data?.find((c) => c.id === id)?.name ?? '顧客';

  const items: AlertItem[] = [];

  for (const i of lowStock.data ?? []) {
    items.push({
      id: `low-${i.id}`,
      severity: i.qty === 0 ? 'danger' : 'warn',
      title: i.qty === 0 ? `耗材斷貨：${i.name}` : `耗材低於低水位：${i.name}`,
      detail: `現量 ${i.qty} / 低水位 ${i.lowAt}`,
      to: '/inventory',
    });
  }
  for (const c of certs.data ?? []) {
    items.push({ id: `cert-${c.id}`, severity: 'danger', title: `證照已過期：${staffName(c.staffId)}`, detail: `${c.skill} · ${c.expireAt.slice(0, 10)}`, to: '/scheduling' });
  }
  for (const r of (requests.data ?? []).filter((x) => x.status === 'PENDING')) {
    items.push({ id: `req-${r.id}`, severity: 'warn', title: `排班申請待審：${staffName(r.staffId)}`, detail: `${REQ_TYPE[r.type] ?? r.type} · ${r.startDate.slice(0, 10)}`, to: '/scheduling' });
  }
  for (const c of (complaints.data ?? []).filter((x) => x.status !== 'RESOLVED')) {
    const overdue = !!c.dueAt && new Date(c.dueAt).getTime() < now;
    items.push({
      id: `cmp-${c.id}`,
      severity: overdue ? 'danger' : 'warn',
      title: `${overdue ? '客訴逾期：' : '客訴處理中：'}${custName(c.customerId)}`,
      detail: `${c.issue}${c.dueAt ? ` · SLA ${c.dueAt.slice(0, 10)}` : ''}`,
      to: '/compliance',
    });
  }
  for (const d of (coverage.data ?? []).filter((x) => x.understaffed && new Date(x.date).getTime() >= now - 86400000)) {
    items.push({ id: `cov-${d.date}`, severity: 'info', title: `人力不足：${d.date}`, detail: d.reasons.join('、'), to: '/scheduling' });
  }

  const rank: Record<AlertSeverity, number> = { danger: 0, warn: 1, info: 2 };
  items.sort((a, b) => rank[a.severity] - rank[b.severity]);

  return { items, total: items.length, isLoading: lowStock.isLoading || complaints.isLoading };
}
